import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { InfoIcon } from './icons'

interface InfoHintProps {
  text: ReactNode
  size?: number
  /** Доступное имя триггера (если text — не строка). */
  label?: string
}

/**
 * Иконка-подсказка «(!)»: при наведении/фокусе/тапе показывает тултип. Тултип
 * рендерится в портал (document.body) с `position: fixed` — не обрезается контейнерами
 * с `overflow` (напр. скролл-обёртка таблицы). Доступно с клавиатуры (триггер — кнопка,
 * tooltip связан через aria-describedby); закрывается по Escape/скроллу/тапу вне (мобайл).
 */
export function InfoHint({ text, size = 14, label }: InfoHintProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const ref = useRef<HTMLButtonElement>(null)
  const id = useId()

  function place() {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const halfW = 130 // ≈ половина max-width тултипа — для клампа в вьюпорт
    const left = Math.min(Math.max(r.left + r.width / 2, 12 + halfW), window.innerWidth - 12 - halfW)
    setPos({ top: r.bottom + 8, left })
  }

  function show() {
    place()
    setOpen(true)
  }
  function hide() {
    setOpen(false)
  }

  // Закрывать при скролле (fixed-тултип иначе «отцепится»), по Escape и по тапу/клику
  // вне триггера (на мобиле hover нет — иначе тултип нечем закрыть, кроме повторного тапа).
  useEffect(() => {
    if (!open) return
    const onScroll = () => setOpen(false)
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    const onPointer = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) setOpen(false)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        ref={ref}
        className="hint-trigger"
        aria-label={typeof text === 'string' ? text : label ?? 'Подсказка'}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setOpen((v) => !v)
          if (!open) place()
        }}
      >
        <InfoIcon size={size} />
      </button>
      {open &&
        createPortal(
          <div id={id} role="tooltip" className="tip-pop" style={{ top: pos.top, left: pos.left }}>
            {text}
          </div>,
          document.body,
        )}
    </>
  )
}
